import { useEffect, useMemo, useState } from 'preact/hooks';
import { Credit, Placeholder } from './chrome';
import { watchClass, watchShow } from './firebase';
import {
  classDayKey,
  dayKey,
  formatDayKey,
  formatStartTime,
  isValidDateParam,
  STATUS_LABEL,
} from './format';
import { sliceRows, type RowLimit, type WidgetParams } from './params';
import { ResultsTable } from './results-table';
import type { PublicResultsClassFS, PublicShowClassSummaryFS, PublicShowFS } from './types';

/** How often `today` re-checks the venue's date, so an open page rolls over at midnight. */
const TODAY_REFRESH_MS = 60_000;

/** Ring names are typed by hand on both sides, so case and stray spaces must not matter. */
function normalizeRing(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/**
 * The ring's running order: start time first, then class number. A class with no
 * start time of its own sorts after the timed ones, by number, rather than at the
 * top as midnight.
 */
function compareClasses(a: PublicShowClassSummaryFS, b: PublicShowClassSummaryFS): number {
  const timeA = a.startTime ? Date.parse(a.startTime) : Number.POSITIVE_INFINITY;
  const timeB = b.startTime ? Date.parse(b.startTime) : Number.POSITIVE_INFINITY;
  if (timeA !== timeB) return timeA < timeB ? -1 : 1;

  const numA = Number(a.classNumber);
  const numB = Number(b.classNumber);
  if (Number.isFinite(numA) && Number.isFinite(numB) && numA !== numB) return numA - numB;
  return a.classNumber.localeCompare(b.classNumber, undefined, { numeric: true });
}

/** The venue's calendar day for `today`, refreshed so a page left open rolls over. */
function useToday(timezone: string | undefined, enabled: boolean): string {
  const [today, setToday] = useState(() => dayKey(new Date(), timezone));

  useEffect(() => {
    if (!enabled) return undefined;
    setToday(dayKey(new Date(), timezone));
    const timer = setInterval(() => setToday(dayKey(new Date(), timezone)), TODAY_REFRESH_MS);
    return () => clearInterval(timer);
  }, [timezone, enabled]);

  return today;
}

/**
 * One ring's classes for one day, as an accordion.
 *
 * Only the show index is read up front, and it already carries everything a row
 * needs, so the list costs one document however many classes the ring runs. A
 * class's results are subscribed to only while it is open, and only one is open
 * at a time, so a viewer holds at most two listeners.
 */
export function DayView({ params }: { params: WidgetParams }) {
  const rawDate = params.date ?? '';
  const dateIsValid = isValidDateParam(rawDate);
  const isToday = rawDate.toLowerCase() === 'today';

  const [show, setShow] = useState<PublicShowFS | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openClassId, setOpenClassId] = useState<string | null>(null);

  const canLoad = Boolean(params.showId && params.ring && dateIsValid);

  useEffect(() => {
    if (!canLoad || !params.showId) return undefined;
    return watchShow(
      params.showId,
      (data) => {
        setShow(data);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }, [params.showId, canLoad]);

  const today = useToday(show?.timezone, isToday);
  const selectedDay = isToday ? today : rawDate;

  const classes = useMemo(() => {
    const ring = normalizeRing(params.ring);
    return (show?.classes ?? [])
      .filter((cls) => normalizeRing(cls.ring) === ring)
      .filter((cls) => classDayKey(cls.classDate, show?.timezone) === selectedDay)
      .sort(compareClasses);
  }, [show, params.ring, selectedDay]);

  // A class can leave the list while open — moved to another ring or day, or
  // deleted. Close it rather than keep a listener on something no longer shown.
  useEffect(() => {
    if (openClassId && !classes.some((cls) => cls.classId === openClassId)) {
      setOpenClassId(null);
    }
  }, [classes, openClassId]);

  if (!params.showId) {
    return <Placeholder tone="error" message="This embed is missing a show parameter." />;
  }
  if (!params.ring) {
    return <Placeholder tone="error" message="This embed is missing a ring parameter." />;
  }
  if (!dateIsValid) {
    return (
      <Placeholder
        tone="error"
        message={`Invalid date "${rawDate}". Use YYYY-MM-DD or today.`}
      />
    );
  }
  if (error) {
    return <Placeholder tone="error" message={`Results are unavailable right now. (${error})`} />;
  }
  if (loading) {
    return <Placeholder tone="loading" message="Loading classes…" />;
  }
  if (!show) {
    return <Placeholder message="Classes for this show have not been published yet." />;
  }

  // Print the ring as the show spells it, not as the embed code happened to.
  const ringLabel = classes[0]?.ring || params.ring;

  return (
    <div class="widget">
      <p class="eyebrow">Class Results</p>

      <header class="header">
        <h1 class="title">{show.showName}</h1>
        <div class="meta-row">
          <span class="meta">{[ringLabel, formatDayKey(selectedDay)].join(' · ')}</span>
        </div>
      </header>

      {classes.length === 0 ? (
        <Placeholder message={`No classes in ${ringLabel} on ${formatDayKey(selectedDay)}.`} />
      ) : (
        <ul class="class-list">
          {classes.map((cls) => (
            <ClassItem
              key={cls.classId}
              summary={cls}
              timezone={show.timezone}
              limit={params.limit}
              open={cls.classId === openClassId}
              onToggle={() =>
                setOpenClassId((current) => (current === cls.classId ? null : cls.classId))
              }
            />
          ))}
        </ul>
      )}

      <footer class="footer">
        <span>
          {classes.length} {classes.length === 1 ? 'class' : 'classes'}
        </span>
        <Credit />
      </footer>
    </div>
  );
}

function ClassItem({
  summary,
  timezone,
  limit,
  open,
  onToggle,
}: {
  summary: PublicShowClassSummaryFS;
  timezone: string | undefined;
  limit: RowLimit;
  open: boolean;
  onToggle: () => void;
}) {
  const panelId = `panel-${summary.classId}`;
  const buttonId = `class-${summary.classId}`;
  const startTime = formatStartTime(summary.startTime, timezone);

  return (
    <li class={open ? 'class-item class-item--open' : 'class-item'}>
      <h2 class="class-heading">
        <button
          type="button"
          id={buttonId}
          class="class-toggle"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
        >
          <span class="class-label">
            {summary.classNumber && <span class="class-number-chip">#{summary.classNumber}</span>}
            <span class="class-name">
              {[summary.heightLabel, summary.className].filter(Boolean).join(' ')}
            </span>
          </span>
          <span class="class-aside">
            {startTime && <span class="class-time">{startTime}</span>}
            <span class={`badge badge--${summary.status}`}>{STATUS_LABEL[summary.status]}</span>
            <span class="chevron" aria-hidden="true" />
          </span>
        </button>
      </h2>
      {open && (
        <div id={panelId} role="region" aria-labelledby={buttonId} class="class-panel">
          <ClassResults classId={summary.classId} limit={limit} />
        </div>
      )}
    </li>
  );
}

/** Mounted only while its class is open, so a collapsed class costs no reads. */
function ClassResults({ classId, limit }: { classId: string; limit: RowLimit }) {
  const [classDoc, setClassDoc] = useState<PublicResultsClassFS | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () =>
      watchClass(
        classId,
        (data) => {
          setClassDoc(data);
          setLoading(false);
        },
        (err) => {
          setError(err.message);
          setLoading(false);
        },
      ),
    [classId],
  );

  if (error) {
    return <Placeholder tone="error" message={`Results are unavailable right now. (${error})`} />;
  }
  if (loading) {
    return <Placeholder tone="loading" message="Loading results…" />;
  }
  if (!classDoc || classDoc.rows.length === 0) {
    return <Placeholder message="No results yet." />;
  }

  const visibleRows = sliceRows(classDoc.rows, limit);
  const isTruncated = classDoc.rowCount > visibleRows.length;

  return (
    <>
      <ResultsTable rows={visibleRows} status={classDoc.status} />
      <p class="class-count">
        {isTruncated ? `Top ${visibleRows.length} of ${classDoc.rowCount}` : classDoc.rowCount}{' '}
        entries
      </p>
    </>
  );
}
